import React from 'react';
import styles from '../ClientManagement.module.css';

/**
 * ClientStatistics Component
 * Displays comprehensive statistics about clients including document counts
 * 
 * @param {Object} statistics - Statistics object with client counts
 * @param {number} statistics.total - Total number of clients
 * @param {number} statistics.active - Number of active clients
 * @param {number} statistics.inactive - Number of inactive clients
 * @param {number} statistics.recentClients - Clients added in last 30 days
 * @param {number} statistics.totalDocuments - Total documents across all clients (from VW_Client_Summary)
 * @param {number} statistics.clientsWithDocuments - Number of clients with at least one document
 * @param {number} statistics.clientsWithoutDocuments - Number of clients without documents
 * @param {string} statistics.avgDocumentsPerClient - Average documents per client
 * @param {boolean} loading - Loading state
 */
const ClientStatistics = ({ statistics, loading }) => {
    if (loading) {
        return (
            <div className={styles.statisticsContainer}>
                <div className={styles.statisticsLoading}>
                    <div className={styles.spinner}></div>
                    <span>Loading statistics...</span>
                </div>
            </div>
        );
    }

    if (!statistics) {
        return null;
    }

    return (
        <div className={styles.statisticsContainer}>
            <div className={styles.statisticsHeader}>
                <h3>Client Overview</h3>
                <p>Statistics about clients in the system</p>
            </div>
            
            <div className={styles.statisticsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statHeader}>
                        <h4 className={styles.statLabel}>Total Clients</h4>
                        <span className={styles.statValue}>{statistics.total}</span>
                    </div>
                    <div className={styles.statBar}>
                        <div 
                            className={styles.statBarFill}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
                
                <div className={styles.statCard}>
                    <div className={styles.statHeader}>
                        <h4 className={styles.statLabel}>Active Clients</h4>
                        <span className={styles.statValue}>{statistics.active}</span>
                    </div>
                    <div className={styles.statBar}>
                        <div 
                            className={styles.statBarFill}
                            style={{ width: `${statistics.total ? (statistics.active / statistics.total) * 100 : 0}%` }}
                        />
                    </div>
                    <div className={styles.statFooter}>
                        <span className={styles.statPercentage}>
                            {statistics.total ? Math.round((statistics.active / statistics.total) * 100) : 0}% of total
                        </span>
                    </div>
                </div>
                
                <div className={styles.statCard}>
                    <div className={styles.statHeader}>
                        <h4 className={styles.statLabel}>Inactive Clients</h4>
                        <span className={styles.statValue}>{statistics.inactive}</span>
                    </div>
                    <div className={styles.statBar}>
                        <div 
                            className={styles.statBarFill}
                            style={{ width: `${statistics.total ? (statistics.inactive / statistics.total) * 100 : 0}%` }}
                        />
                    </div>
                    <div className={styles.statFooter}>
                        <span className={styles.statPercentage}>
                            {statistics.total ? Math.round((statistics.inactive / statistics.total) * 100) : 0}% of total
                        </span>
                    </div>
                </div>
                
                <div className={styles.statCard}>
                    <div className={styles.statHeader}>
                        <h4 className={styles.statLabel}>Recent Clients</h4>
                        <span className={styles.statValue}>{statistics.recentClients || 0}</span>
                    </div>
                    <div className={styles.statBar}>
                        <div 
                            className={styles.statBarFill}
                            style={{ width: `${statistics.total ? ((statistics.recentClients || 0) / statistics.total) * 100 : 0}%` }}
                        />
                    </div>
                    <div className={styles.statFooter}>
                        <span className={styles.statPercentage}>
                            In the last 30 days
                        </span>
                    </div>
                </div>
                
                {/* Document Statistics - from VW_Client_Summary */}
                {statistics.totalDocuments !== undefined && (
                    <>
                        <div className={styles.statCard}>
                            <div className={styles.statHeader}>
                                <h4 className={styles.statLabel}>Total Documents</h4>
                                <span className={styles.statValue}>{statistics.totalDocuments || 0}</span>
                            </div>
                            <div className={styles.statBar}>
                                <div 
                                    className={styles.statBarFill}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className={styles.statFooter}>
                                <span className={styles.statPercentage}>
                                    Across all clients
                                </span>
                            </div>
                        </div>
                        
                        <div className={styles.statCard}>
                            <div className={styles.statHeader}>
                                <h4 className={styles.statLabel}>Clients with Documents</h4>
                                <span className={styles.statValue}>{statistics.clientsWithDocuments || 0}</span>
                            </div>
                            <div className={styles.statBar}>
                                <div 
                                    className={styles.statBarFill}
                                    style={{ width: `${statistics.total ? ((statistics.clientsWithDocuments || 0) / statistics.total) * 100 : 0}%` }}
                                />
                            </div>
                            <div className={styles.statFooter}>
                                <span className={styles.statPercentage}>
                                    {statistics.total ? Math.round(((statistics.clientsWithDocuments || 0) / statistics.total) * 100) : 0}% have documents
                                </span>
                            </div>
                        </div>
                        
                        <div className={styles.statCard}>
                            <div className={styles.statHeader}>
                                <h4 className={styles.statLabel}>Avg Documents/Client</h4>
                                <span className={styles.statValue}>{statistics.avgDocumentsPerClient || '0.0'}</span>
                            </div>
                            <div className={styles.statBar}>
                                <div 
                                    className={styles.statBarFill}
                                    style={{ width: '75%' }}
                                />
                            </div>
                            <div className={styles.statFooter}>
                                <span className={styles.statPercentage}>
                                    Average per client
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>
            
            <div className={styles.statisticsSummary}>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Client Status:</span>
                    <span className={styles.summaryValue}>
                        {statistics.active > statistics.inactive ? 
                            `${statistics.active - statistics.inactive} more active than inactive` :
                            statistics.inactive > statistics.active ?
                            `${statistics.inactive - statistics.active} more inactive than active` :
                            'Equal active and inactive clients'
                        }
                    </span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Activity Rate:</span>
                    <span className={styles.summaryValue}>
                        {statistics.total ? 
                            `${Math.round((statistics.active / statistics.total) * 100)}% active` : 
                            'No data'
                        }
                    </span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Growth Trend:</span>
                    <span className={styles.summaryValue}>
                        {statistics.recentClients ? 
                            `${statistics.recentClients} new in 30 days` : 
                            'No recent additions'
                        }
                    </span>
                </div>
                {statistics.totalDocuments !== undefined && (
                    <>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Document Coverage:</span>
                            <span className={styles.summaryValue}>
                                {statistics.clientsWithDocuments && statistics.total ? 
                                    `${Math.round((statistics.clientsWithDocuments / statistics.total) * 100)}% of clients have documents` : 
                                    'No document data'
                                }
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Document Distribution:</span>
                            <span className={styles.summaryValue}>
                                {statistics.totalDocuments && statistics.clientsWithDocuments ? 
                                    `${statistics.totalDocuments} docs across ${statistics.clientsWithDocuments} clients` : 
                                    'No documents'
                                }
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ClientStatistics;
